import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedPayload } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  mergeMap,
  pluck,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { getRouterParamsId } from '@app/app.reducer';
import { attachmentsGet } from '@collections/attachments';
import { DangerAlert, SuccessAlert } from '@shared/alert';
import { Attachment, JobBoard } from '@shared/models';
import { AttachmentService } from '@shared/services/attachment.service';
import { JobBoardPostService } from '@shared/services/job-board-post.service';
import { JobBoardService } from '@shared/services/job-board.service';

import * as JobBoardActions from './job-board.actions';
import { getJobBoardById, jobBoardsGetJobBoard } from './job-board.selectors';
import { JobService } from '@shared/services/job.service';

@Injectable()
export class JobBoardEffects {
  loadJobBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.loadJobBoards),
      switchMap(({ offset }) =>
        this.jobBoardService.getAllJobBoards(offset).pipe(
          map((jobBoards) => JobBoardActions.loadJobBoardsSuccess({ jobBoards })),
          catchError((error) => of(JobBoardActions.loadJobBoardsFailure({ error }))),
        ),
      ),
    ),
  );

  loadNumberOfJobsForExtendedReferral$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.loadNumberOfJobsForExtendedReferral),
      switchMap(({ jobBoardCanonicalName }) =>
        this.jobService.getAllActiveJobsForExtendedReferral(jobBoardCanonicalName).pipe(
          map((jobsList) =>
            JobBoardActions.loadNumberOfJobsForExtendedReferralSuccess({
              totalElements: jobsList.totalElements,
            }),
          ),
        ),
      ),
    ),
  );

  updateJobBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.updateJobBoard),
      withLatestFrom(this.store.select(getRouterParamsId)),
      switchMap(([{ jobBoard }, id]) => {
        const jobId = !id ? jobBoard.id : id;
        return this.jobBoardService.updateJobBoard(jobBoard, jobId).pipe(
          map((jobBoardToUpdate) =>
            JobBoardActions.updateJobBoardSuccess({ jobBoard: jobBoardToUpdate }),
          ),
          catchError((error) => of(JobBoardActions.updateJobBoardFailure({ error }))),
        );
      }),
    ),
  );

  updateJobBoardSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.updateJobBoardSuccess),
      map(() => new SuccessAlert('Job board has been updated')),
    ),
  );

  updateJobBoardFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.updateJobBoardFailure),
      map(() => new DangerAlert('There was a problem while updating the job board')),
    ),
  );

  getActiveJobBoardsForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.getActiveJobBoardsForJob),
      mergeMap(({ jobId }) =>
        this.jobBoardPostService.getJobBoardsForJob(jobId).pipe(
          map((jobBoardsForJob) =>
            JobBoardActions.getActiveJobBoardsForJobSuccess({ jobBoardsForJob }),
          ),
          catchError((error) => of(JobBoardActions.getActiveJobBoardsForJobFailure({ error }))),
        ),
      ),
    ),
  );

  getAllJobBoards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.loadJobBoardsWithoutPagination),
      mergeMap(() =>
        this.jobBoardService.getAllJobBoardsWithoutPagination().pipe(
          map((allJobBoards) =>
            JobBoardActions.loadJobBoardsWithoutPaginationSuccess({ allJobBoards }),
          ),
          catchError((error) =>
            of(JobBoardActions.loadJobBoardsWithoutPaginationFailure({ error })),
          ),
        ),
      ),
    ),
  );

  updateJobBoardPostForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.updateJobBoardPostForJob),
      switchMap(({ partialJobBoardPost }) =>
        this.jobBoardPostService.updateJobBoardPostForJob(partialJobBoardPost).pipe(
          map((jobBoardPost) => JobBoardActions.updateJobBoardPostForJobSuccess({ jobBoardPost })),
          catchError((error) => of(JobBoardActions.updateJobBoardPostForJobFailure({ error }))),
        ),
      ),
    ),
  );

  updateJobBoardPostForSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.updateJobBoardPostForJobSuccess),
      map(() => new SuccessAlert('job board post for job has been updated')),
    ),
  );

  updateJobBoardPostForFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.updateJobBoardPostForJobFailure),
      map(() => new DangerAlert('something gone wrong with updating')),
    ),
  );

  getJobBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.getJobBoard),
      switchMap(({ jobBoardId }) =>
        this.store.select(getJobBoardById, { id: jobBoardId }).pipe(
          switchMap((jobBoard) => {
            if (!jobBoard) {
              return this.jobBoardService.get({ id: jobBoardId }).pipe(
                map((response) => JobBoardActions.getJobBoardSuccess({ jobBoard: response })),
                catchError((error) => of(JobBoardActions.getJobBoardFailure({ error }))),
              );
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  uploadLogoAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.uploadLogoJobBoard),
      switchMap(({ attachment, file }) =>
        this.attachmentService.save(file, attachment).pipe(
          map((a: Attachment) => JobBoardActions.uploadLogoJobBoardSuccess({ attachment: a })),
          catchError((error) => of(JobBoardActions.uploadLogoJobBoardFailure({ error }))),
        ),
      ),
    ),
  );

  uploadHeaderAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.uploadHeaderJobBoard),
      switchMap(({ attachment, file }) =>
        this.attachmentService.save(file, attachment).pipe(
          map((a: Attachment) => JobBoardActions.uploadHeaderJobBoardSuccess({ attachment: a })),
          catchError((error) => of(JobBoardActions.uploadHeaderJobBoardFailure({ error }))),
        ),
      ),
    ),
  );

  uploadJobBoardImageFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        JobBoardActions.uploadHeaderJobBoardFailure,
        JobBoardActions.uploadLogoJobBoardFailure,
      ),
      map(() => new DangerAlert('There was a problem while uploading an image')),
    ),
  );

  createJobBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.createJobBoard),
      switchMap(({ jobBoard }) =>
        this.jobBoardService.post<JobBoard>({ body: jobBoard }).pipe(
          mergeMap((jb: JobBoard) => [
            JobBoardActions.createJobBoardSuccess({ jobBoard: jb }),
            // need to reload because of pagination
            JobBoardActions.loadJobBoards({ offset: 0 }),
          ]),
          catchError((error) => of(JobBoardActions.createJobBoardFailure({ error }))),
        ),
      ),
    ),
  );

  createJobBoardSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.createJobBoardSuccess),
      map(() => new SuccessAlert('Job board created')),
    ),
  );

  createJobBoardFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.createJobBoardFailure),
      map(() => new DangerAlert('There was a problem while creating job board')),
    ),
  );

  selectJobBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      pluck('payload'),
      filter((payload: RouterNavigatedPayload<{ url: string; params: { id: string } }>) =>
        payload?.event?.url.startsWith('/job-boards/job-board'),
      ),
      switchMap(({ routerState }) => {
        const id = +routerState.params.id;
        return forkJoin([of(id), this.store.select(jobBoardsGetJobBoard, { id }).pipe(first())]);
      }),
      map(([id, jobBoard]: [number, JobBoard]) => {
        if (!jobBoard && !!id) {
          return JobBoardActions.getJobBoard({ jobBoardId: id });
        } else if (!id) {
          return JobBoardActions.unselectJobBoard();
        }
        return JobBoardActions.selectJobBoard({ jobBoard });
      }),
    ),
  );

  redirectOnJobBoardSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JobBoardActions.createJobBoardSuccess, JobBoardActions.updateJobBoardSuccess),
        map(() => this.router.navigate(['/job-boards'])),
      ),
    { dispatch: false },
  );

  getImageJobBoard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobBoardActions.getImageJobBoard),
      map(({ id }) => attachmentsGet({ id })),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<{}>,
    private jobBoardService: JobBoardService,
    private jobBoardPostService: JobBoardPostService,
    private attachmentService: AttachmentService,
    private router: Router,
    private jobService: JobService,
  ) {}
}
